import { WorkbenchRuntimeError } from '../../shared/errors';

/**
 * @name CacheService
 *
 * @description Make requests to retrieve data while caching the results during your application's runtime
 *
 * @example
 * import { CacheService } from '@workbench/core';
 *
 * const cacheService = new CacheService();
 *
 * cacheService
 *   .get('https://url.com/contacts.json')
 *   .then(rawData => console.log(rawData))
 *   .catch(err => console.error(err));
 */
export class CacheService {
  public static cached = {};
  public static pending = {};

  public get(resource: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.isResourceCached(resource)) {
        resolve((CacheService.cached as any)[resource]);
      } else if (this.isRequestPending(resource)) {
        this.usePendingRequest(resource, resolve, reject);
      } else {
        this.requestResource(resource);
        this.usePendingRequest(resource, resolve, reject);
      }
    });
  }

  private isRequestPending(resource: string): boolean {
    return Boolean((CacheService.pending as any)[resource]);
  }

  private isResourceCached(resource: string): boolean {
    return Boolean((CacheService.cached as any)[resource]);
  }

  private usePendingRequest(resource: string, resolve: (value?: any) => void, reject: (value?: any) => void): void {
    (CacheService.pending as any)[resource]
      .then((content: any) => {
        this.cacheResource(resource, content);
        this.clearPendingRequest(resource);
        resolve((CacheService.cached as any)[resource]);
      })
      .catch(() => {
        this.clearPendingRequest(resource);
        reject(new WorkbenchRuntimeError(`Failed to load resource: '${resource}'`, 'cache-service'));
      });
  }

  private requestResource(resource: string): void {
    (CacheService.pending as any)[resource] = new Promise((resolve, reject) => {
      fetch(resource)
        .then((res) => {
          if (res.ok) {
            res
              .text()
              .then((text) => resolve(text))
              .catch(() => reject());
          } else {
            reject();
          }
        })
        .catch(() => reject());
    });
  }

  private clearPendingRequest(resource: string): void {
    delete (CacheService.pending as any)[resource];
  }

  private cacheResource(resource: string, content: string): void {
    (CacheService.cached as any)[resource] = content;
    delete (CacheService.pending as any)[resource];
  }
}
